import { graphql } from 'react-relay';
import { useMemo, useEffect, useState } from 'react';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { useMentionsNotificationsActions, type MentionType } from './useMentionsNotifications.tsx';
import type {
	useUsersContentPermissionsUpdateContentPermissionsMutation as useUsersContentPermissionsUpdateContentPermissionsMutation$data,
	useUsersContentPermissionsUpdateContentPermissionsMutationVariables as useUsersContentPermissionsUpdateContentPermissionsMutation$variables,
} from './__types__/useUsersContentPermissionsUpdateContentPermissionsMutation.ts';
import {
	type useUsersContentPermissionsNewUserHasRestrictionsQuery$data,
	type useUsersContentPermissionsNewUserHasRestrictionsQuery$variables,
} from './__generated__/useUsersContentPermissionsNewUserHasRestrictionsQuery.graphql.ts';
import {
	type useUsersContentPermissionsContentPermissionsQuery$data,
	type useUsersContentPermissionsContentPermissionsQuery$variables,
} from './__generated__/useUsersContentPermissionsContentPermissionsQuery.graphql.ts';
const useUsersContentPermissionsContentPermissionsQuery = graphql`
	query useUsersContentPermissionsContentPermissionsQuery($accountIds: [String]!, $contentId: ID!) {
		contentPermissions(contentId: $contentId) {
			contentAccess
			subjectsWithEffectiveContentPermissions {
				nodes {
					id
					displayName
					user {
						profilePicture {
							path
						}
					}
				}
			}
		}
		usersWithContentRestrictions(accountIds: $accountIds, contentId: $contentId) {
			hasSpaceViewPermission
			restrictingContent {
				id
				hasRestrictions
			}
			accountId
			displayName
			profilePicture {
				path
			}
		}
	}
`;
const useUsersContentPermissionsNewUserHasRestrictionsQuery = graphql`
	query useUsersContentPermissionsNewUserHasRestrictionsQuery(
		$cloudId: ID!
		$accountIds: [String]!
		$resourceId: ID!
		$resourceType: String!
		$permission: String!
	) {
		confluence_usersHavePermission(
			cloudId: $cloudId
			principalIds: $accountIds
			resourceId: $resourceId
			resourceType: $resourceType
			permission: $permission
		) {
			usersHavePermissionList {
				principalId
				hasPermission
			}
		}
		confluenceUsers(accountIds: $accountIds) {
			nodes {
				displayName
				profilePicture {
					path
				}
				... on KnownUser {
					accountId
				}
			}
		}
	}
`;
const useUsersContentPermissionsUpdateContentPermissionsMutation = graphql`
	mutation useUsersContentPermissionsUpdateContentPermissionsMutation(
		$contentId: ID!
		$input: [UpdateContentPermissionsInput]!
	) {
		updateContentPermissions(contentId: $contentId, input: $input) {
			status
		}
	}
`;

type ContentPermissionsQueryProps = {
	contentId: string;
	isUpdatedMentionsInLivePagesEnabled: boolean;
	isLivePage: boolean;
	currentUserId: string | null;
	mentionsToDisplay: MentionType[];
	source: string;
};

type UserWithViewPermissions = {
	id: string;
	hasViewRestrictions: boolean;
	displayName: string;
	userPhoto: string;
};

export const getUsersWithViewPermissions = async ({
	accountIds,
	contentId,
	cloudId,
	setAddedMentions,
	setAddedTasks,
}: {
	accountIds: string[];
	contentId: string;
	cloudId: string;
	setAddedMentions: (mentions: MentionType[]) => void;
	setAddedTasks: (tasks: MentionType[]) => void;
}): Promise<UserWithViewPermissions[] | undefined> => {
	try {
		const { data } = await queryInterop<
			useUsersContentPermissionsNewUserHasRestrictionsQuery$data,
			useUsersContentPermissionsNewUserHasRestrictionsQuery$variables
		>({
			query: useUsersContentPermissionsNewUserHasRestrictionsQuery,
			variables: {
				cloudId,
				accountIds,
				resourceId: contentId,
				resourceType: 'PAGE',
				permission: 'VIEW',
			},
			fetchPolicy: 'network-only', // we'll need to check user's permissions everytime to correctly display the correct flag contents in case the user's permission changed
		});

		const usersWithViewPermissions = new Set(
			data?.confluence_usersHavePermission?.usersHavePermissionList
				?.filter((user) => user?.hasPermission)
				.map((user) => user?.principalId),
		);

		return data?.confluenceUsers?.nodes?.reduce<UserWithViewPermissions[]>((acc, user) => {
			const accountId = user?.accountId;
			if (user && accountId && usersWithViewPermissions.has(accountId)) {
				acc.push({
					id: accountId,
					hasViewRestrictions: false,
					displayName: user?.displayName ?? '',
					userPhoto: user?.profilePicture?.path ?? '',
				});
			}
			return acc;
		}, []);
	} catch {
		//reset state so query won't keep erroring out on same data
		void setAddedMentions([]);
		void setAddedTasks([]);
		return [];
	}
};

export const useUsersContentPermissions = ({
	contentId,
	isUpdatedMentionsInLivePagesEnabled,
	isLivePage,
	currentUserId,
	mentionsToDisplay,
	source,
}: ContentPermissionsQueryProps): {
	updatedUsersContentPermissionsError: ApolloError | undefined;
	updatedUsersContentPermissionsLoading: boolean;
	updateUsersContentPermissionMutation: MutationFunctionInterop<
		useUsersContentPermissionsUpdateContentPermissionsMutation$data,
		useUsersContentPermissionsUpdateContentPermissionsMutation$variables
	>;
} => {
	//TODO: save the permissions of users in a separate state if it's already available, so that we don't have to query forit again
	// The suppression flag is set by the gated editor mention path; keep those mentions out of
	// notification/access-warning permission work while preserving general mention tracking.
	const mentionsToNotify = useMemo(
		() => mentionsToDisplay.filter((mention) => !mention.shouldSuppressMentionNotification),
		[mentionsToDisplay],
	);
	const mentionedUserIds = useMemo(() => {
		const allUniqueMentionedUserIds = new Set(mentionsToNotify.map((mention) => mention.id));
		return Array.from(allUniqueMentionedUserIds);
	}, [mentionsToNotify]);
	const [unrestrictedUsers, setUnrestrictedUsers] = useState<UserWithViewPermissions[]>([]);
	const [isUnrestrictedUsersLoading, setIsUnrestrictedUsersLoading] = useState<boolean>(false);
	const { cloudId } = useSessionData();

	const {
		setAddedMentions,
		setAddedTasks,
		addUnrestrictedMentions,
		addParentSpaceRestrictedMentions,
		addPageRestrictedMentions,
		addParentSpaceRestrictedTasks,
		addPageRestrictedTasks,
		addUnrestrictedTasks,
	} = useMentionsNotificationsActions();

	//useLazyQuery is being used here because skip was not being respected in useQuery. Known issue in react-apollo: https://github.com/apollographql/react-apollo/issues/3492
	const [
		getContentPermissions,
		{
			data: updatedContentPermissionsData,
			error: updatedUsersContentPermissionsError,
			loading: updatedUsersContentPermissionsLoading,
		},
	] = useLazyQueryInterop<
		useUsersContentPermissionsContentPermissionsQuery$data,
		useUsersContentPermissionsContentPermissionsQuery$variables
	>(useUsersContentPermissionsContentPermissionsQuery, {
		variables: { contentId, accountIds: mentionedUserIds },
		fetchPolicy: 'network-only', // we'll need to check user's permissions everytime to correctly display the correct flag contents in case the user's permission changed
		onError: () => {
			//reset state so query won't keep erroring out on same data
			void setAddedMentions([]);
			void setAddedTasks([]);
		},
	});

	useEffect(() => {
		if (isUpdatedMentionsInLivePagesEnabled && mentionedUserIds.length) {
			getContentPermissions({
				variables: { contentId, accountIds: mentionedUserIds },
			});
		}
	}, [contentId, isUpdatedMentionsInLivePagesEnabled, mentionedUserIds, getContentPermissions]);

	useEffect(() => {
		const fetchUnrestrictedUsers = async () => {
			if (isUpdatedMentionsInLivePagesEnabled && mentionedUserIds.length) {
				setIsUnrestrictedUsersLoading(true);
				try {
					const usersWithViewPermissions = await getUsersWithViewPermissions({
						accountIds: mentionedUserIds,
						contentId,
						cloudId,
						setAddedMentions,
						setAddedTasks,
					});
					setUnrestrictedUsers(usersWithViewPermissions ?? []);
				} finally {
					setIsUnrestrictedUsersLoading(false);
				}
			}
		};

		void fetchUnrestrictedUsers();
	}, [
		contentId,
		isUpdatedMentionsInLivePagesEnabled,
		mentionedUserIds,
		cloudId,
		setAddedMentions,
		setAddedTasks,
	]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [updateUsersContentPermissionMutation] = useMutationInterop<
		useUsersContentPermissionsUpdateContentPermissionsMutation$data,
		useUsersContentPermissionsUpdateContentPermissionsMutation$variables
	>(useUsersContentPermissionsUpdateContentPermissionsMutation, {
		onCompleted: () => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'updateContentPermissions',
					action: 'succeeded',
					source,
					attributes: {
						contentId,
						isLivePage,
					},
				},
			}).fire();
		},
		onError: (error: Error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'updateContentPermissions',
					action: 'failed',
					source,
					attributes: {
						contentId,
						error: error.message,
						isLivePage,
					},
				},
			}).fire();
		},
	});
	const mentionsWithSpaceRestrictions: MentionType[] = [];
	const usersWithSpaceRestrictionsIds = new Set<string>();
	const mentionsWithParentPageRestrictions: MentionType[] = [];
	const usersWithParentPageRestrictionsIds = new Set<string>();
	const usersWithSpaceOrParentRestrictionsIds = new Set<string>();
	const usersWithCurrentPageRestrictionsIds = new Set<string>();
	const usersWithUnrestrictedPageAccessIds = new Set<string>();

	const accountIdToDisplayNameMap = new Map();

	useEffect(() => {
		if (updatedContentPermissionsData && mentionsToNotify.length && !isUnrestrictedUsersLoading) {
			const { contentPermissions, usersWithContentRestrictions: contentRestrictions } =
				updatedContentPermissionsData;
			if (contentPermissions?.contentAccess === 'EVERYONE_NO_ACCESS') {
				contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes?.forEach((user) => {
					if (!user?.id || user.id === currentUserId) {
						return;
					}
					usersWithUnrestrictedPageAccessIds.add(user.id);
					accountIdToDisplayNameMap.set(user.id, {
						displayName: user.displayName ?? '',
						userPhoto: user?.user?.profilePicture?.path ?? '',
					});
				});

				unrestrictedUsers.forEach(
					(user: { id: string; displayName?: string; userPhoto?: string }) => {
						usersWithUnrestrictedPageAccessIds.add(user.id);
						accountIdToDisplayNameMap.set(user.id, {
							displayName: user.displayName ?? '',
							userPhoto: user.userPhoto ?? '',
						});
					},
				);
			}
			contentRestrictions?.forEach((user) => {
				if (!user?.accountId) {
					return;
				}
				accountIdToDisplayNameMap.set(user.accountId, {
					displayName: user.displayName ?? '',
					userPhoto: user?.profilePicture?.path ?? '',
				});
				if (!user.hasSpaceViewPermission) {
					//has space level restriction
					usersWithSpaceRestrictionsIds.add(user.accountId);
					usersWithSpaceOrParentRestrictionsIds.add(user.accountId);
				} else if (user.restrictingContent?.hasRestrictions) {
					//has parent page level restriction
					usersWithParentPageRestrictionsIds.add(user.accountId);
					usersWithSpaceOrParentRestrictionsIds.add(user.accountId);
				} else if (contentPermissions?.contentAccess === 'EVERYONE_NO_ACCESS') {
					//might have page level restriction
					if (!usersWithUnrestrictedPageAccessIds.has(user.accountId)) {
						usersWithCurrentPageRestrictionsIds.add(user.accountId);
					}
				}
			});

			mentionsToNotify.forEach((mention) => {
				const mentionToAdd = {
					...mention,
					displayName: accountIdToDisplayNameMap.get(mention.id)?.displayName ?? '',
					userPhoto: accountIdToDisplayNameMap.get(mention.id)?.userPhoto ?? '',
				};
				if (mention?.taskLocalId) {
					//Buckets added task mentions into different permission groups
					if (usersWithSpaceRestrictionsIds.has(mention.id)) {
						//Users who dont have access to the page due to space restrictions
						void addParentSpaceRestrictedTasks([mentionToAdd]);
					} else if (usersWithParentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to parent page restrictions
						void addParentSpaceRestrictedTasks([mentionToAdd]);
					} else if (usersWithCurrentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to current page restrictions
						void addPageRestrictedTasks([mentionToAdd]);
					} else {
						//Users who have access to the page
						void addUnrestrictedTasks([mentionToAdd]);
					}
				} else {
					//Buckets added general mentions into different permission groups
					if (usersWithSpaceRestrictionsIds.has(mention.id)) {
						//Users who dont have access to the page due to space restrictions
						mentionsWithSpaceRestrictions.push(mentionToAdd);
						void addParentSpaceRestrictedMentions([mentionToAdd]);
					} else if (usersWithParentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to parent page restrictions
						mentionsWithParentPageRestrictions.push(mentionToAdd);
						void addParentSpaceRestrictedMentions([mentionToAdd]);
					} else if (usersWithCurrentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to current page restrictions
						void addPageRestrictedMentions([mentionToAdd]);
					} else {
						//Users who have access to the page
						void addUnrestrictedMentions([mentionToAdd]);
					}
				}
			});

			void setAddedMentions([]);
			void setAddedTasks([]);
			void setUnrestrictedUsers([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedContentPermissionsData, isUnrestrictedUsersLoading, unrestrictedUsers]);

	return {
		updatedUsersContentPermissionsError,
		updatedUsersContentPermissionsLoading,
		updateUsersContentPermissionMutation,
	};
};
